// HeatAging.jsx: Heat aging register (2026-09-29).
// Every heat on file from GET /api/heats (tenant scoped), how many days
// since it was received, and whether its status is still open (unverified,
// quarantine or at risk). Records only.

import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import ApiCall from "../../utils/apicall";
import { StatusPill } from "../../components/WeldAssure";
import RegisterShell, { fmtDate, daysSince } from "./RegisterShell";

const OPEN = ["UNVERIFIED", "QUARANTINE", "AT_RISK", "PENDING"];
const kindFor = (s) => (s === "AT_RISK" || s === "QUARANTINE" ? "danger" : OPEN.includes(s) ? "warning" : s ? "success" : "neutral");

export default function HeatAging() {
  const [heats, setHeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await ApiCall.get("/heats");
        setHeats(res.data?.data?.heats || []);
      } catch (err) { setError(err?.response?.data?.message || "Could not load heats."); }
      finally { setLoading(false); }
    })();
  }, []);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return heats
      .map((h) => ({ ...h, age: daysSince(h.receivedDate) }))
      .filter((h) => !q || [h.heatNumber, h.grade, h.shape, h.supplier, h.cmtrCertNumber].some((v) => String(v || "").toLowerCase().includes(q)))
      .sort((a, b) => (b.age ?? -1) - (a.age ?? -1));
  }, [heats, search]);

  const open = heats.filter((h) => OPEN.includes(h.status));
  const kpis = [
    { label: "HEATS ON FILE", value: heats.length },
    { label: "STATUS OPEN", value: open.length },
    { label: "OPEN OVER 30 DAYS", value: open.filter((h) => (daysSince(h.receivedDate) ?? 0) > 30).length },
    { label: "NO CMTR NUMBER", value: heats.filter((h) => !h.cmtrCertNumber).length },
  ];

  return (
    <RegisterShell
      title="Heat Aging"
      subtitle="How long each heat has been in the shop, and whether its status is still open."
      guide={{ why: "See heats that have sat unverified, in quarantine or at risk.", who: "Receivers and QC.", what: "Each heat, its CMTR number, status and days since receipt.", how: "Sorted oldest first. Search by heat, grade or supplier.", when: "Weekly, and before material issue.", standard: "Material identification and traceability" }}
      kpis={kpis}
      columns={["HEAT", "GRADE / SHAPE", "SUPPLIER", "CMTR", "RECEIVED", "DAYS", "STATUS"]}
      rows={rows}
      loading={loading}
      error={error}
      search={search}
      onSearch={setSearch}
      searchPlaceholder="Heat, grade or supplier"
      emptyText="No heats on file. Heats appear here when they are received."
      footnote="Days are counted from the received date on the heat record. Status comes from the heat record as entered."
      renderRow={(h) => (
        <tr key={h.id}>
          <td style={{ fontFamily: "monospace" }}>{h.heatNumber}</td>
          <td>{[h.grade, h.shape].filter(Boolean).join(" / ")}</td>
          <td>{h.supplier || ""}</td>
          <td>{h.cmtrCertNumber || <Box component="span" sx={{ color: "#B02020" }}>none recorded</Box>}</td>
          <td>{fmtDate(h.receivedDate)}</td>
          <td>{h.age ?? ""}</td>
          <td><StatusPill label={h.status || "not set"} kind={kindFor(h.status)} /></td>
        </tr>
      )}
    />
  );
}
